import type { UseFormReturn } from 'react-hook-form';
import type { CustomerModel } from 'src/types/customer';

import { useEffect } from 'react';

import Grid from '@mui/material/Grid';
import {
  Box,
  Card,
  Radio,
  Checkbox,
  FormLabel,
  Typography,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from '@mui/material';

import usePlatforms from 'src/hooks/use-platforms';

import { Form, Field } from 'src/components/hook-form';

import { PaymentMethodEnum } from './order-new-edit-form';

import type { BillingSchemaType } from './order-new-edit-form';

interface BillingDetailsProps {
  billingMethods: UseFormReturn<BillingSchemaType>;
  billingMethod: 'email' | 'platform';
  onBillingMethodChange: (method: 'email' | 'platform') => void;
  customerOptions: CustomerModel[];
  loadingCustomers: boolean;
  onSearchCustomer: (value: string) => void;
  onCustomerSelect: (customer: CustomerModel | null) => void;
  onBillingSubmit: () => void;
  paymentMethod: PaymentMethodEnum;
  onPaymentMethodChange: (method: PaymentMethodEnum) => void;
}

export default function OrderBillingDetails({
  billingMethods,
  billingMethod,
  onBillingMethodChange,
  customerOptions,
  loadingCustomers,
  onSearchCustomer,
  onCustomerSelect,
  onBillingSubmit,
  paymentMethod,
  onPaymentMethodChange,
}: BillingDetailsProps) {
  const { platforms, loading: loadingPlatforms, onSearchPlatform } = usePlatforms();

  useEffect(() => {
    if (billingMethod === 'platform') {
      // Reset customer selection when switching to platform
      billingMethods.setValue('customerId', undefined);
    }
  }, [billingMethod, billingMethods]);

  return (
    <Form methods={billingMethods} onSubmit={onBillingSubmit}>
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Detalles de Facturación
        </Typography>

        {/* Billing Method Selection */}
        <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={billingMethod === 'email'}
                onChange={() => onBillingMethodChange('email')}
              />
            }
            label="Email"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={billingMethod === 'platform'}
                onChange={() => onBillingMethodChange('platform')}
              />
            }
            label="Plataforma"
          />
        </Box>

        {/* Payment Method Selection - visible only when email method is selected */}
        {billingMethod === 'email' && (
          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Método de Pago</FormLabel>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => onPaymentMethodChange(e.target.value as PaymentMethodEnum)}
              >
                <FormControlLabel
                  value={PaymentMethodEnum.PayPal}
                  control={<Radio />}
                  label="PayPal"
                />
                <FormControlLabel
                  value={PaymentMethodEnum.WiseTransfer}
                  control={<Radio />}
                  label="Wise/Transferencia"
                />
                <FormControlLabel
                  value={PaymentMethodEnum.Pending}
                  control={<Radio />}
                  label="Pendiente de pago"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* Platform Selector - visible only when platform method is selected */}
        {billingMethod === 'platform' && (
          <Box sx={{ mb: 3 }}>
            <Field.Autocomplete
              name="platformId"
              label="Plataforma"
              placeholder="Buscar plataforma"
              options={platforms}
              value={
                billingMethods.watch('platformId')
                  ? platforms.find((p) => p.id === billingMethods.watch('platformId')?.value) ||
                    platforms.find((p) => p.id === billingMethods.watch('platformId')?.id) ||
                    null
                  : null
              }
              getOptionLabel={(option) =>
                // Manejar el error ortográfico en el modelo
                option.company || option.company || ''
              }
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.id === value.id || option.id === value.value;
              }}
              renderOption={(props, option) => (
                <li {...props} key={option?.id || option?.value || ''}>
                  {option.company || option.company || 'Sin nombre'}
                </li>
              )}
              onInputChange={(_, newInputValue) => {
                onSearchPlatform(newInputValue);
              }}
              loading={loadingPlatforms}
            />
          </Box>
        )}

        {/* Conditional rendering of billing fields - only show when email is selected */}
        {billingMethod === 'email' && (
          <Grid container spacing={3}>
            {/* Mostrar cliente actual si existe */}
            {billingMethods.watch('customerId') && (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'background.neutral',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Cliente actual:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    ID {billingMethods.watch('customerId')} -{' '}
                    {billingMethods.watch('firstName')} {billingMethods.watch('lastName')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Usa el campo de abajo para cambiar el cliente si es necesario
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Customer Selector with loading state */}
            <Grid size={{ xs: 12 }}>
              <Field.Autocomplete
                name="customerId"
                label="Cambiar cliente (opcional)"
                placeholder="Buscar y seleccionar un cliente diferente"
                options={customerOptions}
                value={
                  billingMethods.watch('customerId')
                    ? customerOptions.find((c) => c.id === billingMethods.watch('customerId')) ||
                      null
                    : null
                }
                getOptionLabel={(option) => {
                  const fullName = `${option.name || ''} ${option.surname || ''}`.trim();
                  return option.company ? `${fullName} (${option.company})` : fullName;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                renderOption={(props, option) => {
                  const fullName = `${option.name || ''} ${option.surname || ''}`.trim();
                  return (
                    <li {...props} key={option?.id || option?.value || ''}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{fullName}</Typography>
                        {option.company && (
                          <Typography variant="caption" color="text.secondary">
                            {option.company}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                onInputChange={(_, newInputValue) => {
                  onSearchCustomer(newInputValue);
                }}
                onChange={(_, newValue) => onCustomerSelect(newValue)}
                loading={loadingCustomers}
              />
            </Grid>

            {/* First Name */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Field.Text name="firstName" label="Nombre" fullWidth />
            </Grid>

            {/* Last Name */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Field.Text name="lastName" label="Apellidos" fullWidth />
            </Grid>

            {/* Company */}
            <Grid size={{ xs: 12 }}>
              <Field.Text name="company" label="Empresa" fullWidth />
            </Grid>

            {/* Email */}
            <Grid size={{ xs: 12 }}>
              <Field.Text name="email" label="Email" type="email" fullWidth />
            </Grid>

            {/* Phone */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Field.Text name="phone" label="Teléfono" fullWidth />
            </Grid>

            {/* Tax ID */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Field.Text name="nif" label="Identificación Fiscal" fullWidth />
            </Grid>

            {/* Country */}
            <Grid size={{ xs: 12, md: 12 }}>
              <Field.CountrySelect
                showTranslation
                name="country"
                label="País"
                fullWidth
                placeholder="Elegir un país"
              />
            </Grid>

            {/* City */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Field.Text name="city" label="Ciudad" fullWidth />
            </Grid>

            {/* Postal Code */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Field.Text name="postalCode" label="Código Postal" fullWidth />
            </Grid>

            {/* Address */}
            <Grid size={{ xs: 12 }}>
              <Field.Text name="address" label="Dirección" fullWidth />
            </Grid>
          </Grid>
        )}
      </Card>
    </Form>
  );
}
